/**
 * Input Rounds Store
 *
 * Manages structured crowd-input collection rounds.
 * Each round scopes specific items + attributes, invites participants,
 * and controls visibility (open / blind) and lifecycle (open → closed → revealed).
 */

import { create } from 'zustand';
import type {
  InputRound,
  InputRoundItem,
  InputRoundParticipant,
  InputRoundStatus,
} from '../../types/prioritization/inputRounds';
import {
  fetchAll,
  insertRecord,
  updateRecord,
  isSupabaseAvailable,
  getCurrentWorkspaceId,
  createInitialSyncState,
  generateUUID,
  type SyncState,
} from '../../lib/supabaseSync';

// ============================================================================
// Table names
// ============================================================================

const ROUNDS_TABLE = 'input_rounds';
const ITEMS_TABLE = 'input_round_items';
const PARTICIPANTS_TABLE = 'input_round_participants';

// ============================================================================
// State interface
// ============================================================================

export interface InputRoundsState {
  rounds: InputRound[];
  roundItems: InputRoundItem[];
  participants: InputRoundParticipant[];
  syncState: SyncState;

  // ===========================
  // Sync
  // ===========================
  fetchFromSupabase: () => Promise<void>;

  // ===========================
  // Round Actions
  // ===========================
  createRound: (data: {
    name: string;
    description?: string;
    visibilityMode?: 'open' | 'blind';
    deadline?: Date | null;
    createdBy: string;
  }) => Promise<string>;

  setRoundStatus: (roundId: string, status: InputRoundStatus) => Promise<void>;
  deleteRound: (roundId: string) => Promise<void>;

  // ===========================
  // Round Items
  // ===========================
  addItemsToRound: (
    roundId: string,
    items: Array<{ itemId: string; attributeKey: string; scaleId?: string | null }>
  ) => Promise<void>;
  removeItemFromRound: (roundItemId: string) => Promise<void>;

  // ===========================
  // Participants
  // ===========================
  addParticipant: (roundId: string, userId: string, userName?: string) => Promise<void>;
  removeParticipant: (participantId: string) => Promise<void>;
  markParticipantSubmitted: (roundId: string, userId: string) => Promise<void>;

  // ===========================
  // Queries
  // ===========================
  getRoundById: (id: string) => InputRound | undefined;
  getItemsForRound: (roundId: string) => InputRoundItem[];
  getParticipantsForRound: (roundId: string) => InputRoundParticipant[];
  getResponseRate: (roundId: string) => { responded: number; total: number; percent: number };
}

// ============================================================================
// Store implementation
// ============================================================================

export const useInputRoundsStore = create<InputRoundsState>((set, get) => ({
  rounds: [],
  roundItems: [],
  participants: [],
  syncState: createInitialSyncState(),

  // ===========================
  // Sync
  // ===========================
  fetchFromSupabase: async () => {
    set((s) => ({ syncState: { ...s.syncState, loading: true, error: null } }));
    try {
      const [roundsResult, itemsResult, participantsResult] = await Promise.all([
        fetchAll<InputRound>(ROUNDS_TABLE, { column: 'created_at', ascending: false }, { filterByWorkspace: true }),
        fetchAll<InputRoundItem>(ITEMS_TABLE, { column: 'created_at', ascending: true }),
        fetchAll<InputRoundParticipant>(PARTICIPANTS_TABLE, { column: 'invited_at', ascending: true }),
      ]);
      set({
        rounds: roundsResult.data ?? [],
        roundItems: itemsResult.data ?? [],
        participants: participantsResult.data ?? [],
        syncState: { loading: false, error: null, lastSynced: new Date() },
      });
    } catch (err) {
      set((s) => ({
        syncState: { ...s.syncState, loading: false, error: err instanceof Error ? err.message : 'Fetch failed' },
      }));
    }
  },

  // ===========================
  // Round Actions
  // ===========================
  createRound: async ({ name, description, visibilityMode = 'open', deadline, createdBy }) => {
    if (!isSupabaseAvailable()) throw new Error('Database not available');
    const workspaceId = getCurrentWorkspaceId();
    const round: InputRound = {
      id: generateUUID(),
      workspaceId: workspaceId ?? undefined,
      name,
      description,
      status: 'open',
      visibilityMode,
      deadline: deadline ?? null,
      createdBy,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    const result = await insertRecord<InputRound>(ROUNDS_TABLE, round);
    if (result.error) throw new Error(result.error);
    set((s) => ({ rounds: [round, ...s.rounds] }));
    return round.id;
  },

  setRoundStatus: async (roundId, status) => {
    if (!isSupabaseAvailable()) throw new Error('Database not available');
    const result = await updateRecord<InputRound>(ROUNDS_TABLE, roundId, {
      status,
      updatedAt: new Date(),
    } as Partial<InputRound>);
    if (result.error) throw new Error(result.error);
    set((s) => ({
      rounds: s.rounds.map((r) => r.id === roundId ? { ...r, status, updatedAt: new Date() } : r),
    }));
  },

  deleteRound: async (roundId) => {
    if (!isSupabaseAvailable()) throw new Error('Database not available');
    const { getSafeSupabaseClient } = await import('../../lib/supabaseSync');
    const supabase = getSafeSupabaseClient();
    if (!supabase) throw new Error('Database client unavailable');
    const { error } = await supabase.from(ROUNDS_TABLE).delete().eq('id', roundId);
    if (error) throw new Error(error.message);
    set((s) => ({
      rounds: s.rounds.filter((r) => r.id !== roundId),
      roundItems: s.roundItems.filter((i) => i.roundId !== roundId),
      participants: s.participants.filter((p) => p.roundId !== roundId),
    }));
  },

  // ===========================
  // Round Items
  // ===========================
  addItemsToRound: async (roundId, items) => {
    if (!isSupabaseAvailable()) throw new Error('Database not available');
    const records: InputRoundItem[] = items.map((item) => ({
      id: generateUUID(),
      roundId,
      itemId: item.itemId,
      attributeKey: item.attributeKey,
      scaleId: item.scaleId ?? null,
      createdAt: new Date(),
    }));
    await Promise.all(records.map((r) => insertRecord<InputRoundItem>(ITEMS_TABLE, r)));
    set((s) => ({ roundItems: [...s.roundItems, ...records] }));
  },

  removeItemFromRound: async (roundItemId) => {
    if (!isSupabaseAvailable()) throw new Error('Database not available');
    const { getSafeSupabaseClient } = await import('../../lib/supabaseSync');
    const supabase = getSafeSupabaseClient();
    if (!supabase) throw new Error('Database client unavailable');
    const { error } = await supabase.from(ITEMS_TABLE).delete().eq('id', roundItemId);
    if (error) throw new Error(error.message);
    set((s) => ({ roundItems: s.roundItems.filter((i) => i.id !== roundItemId) }));
  },

  // ===========================
  // Participants
  // ===========================
  addParticipant: async (roundId, userId, userName) => {
    if (!isSupabaseAvailable()) throw new Error('Database not available');
    const existing = get().participants.find((p) => p.roundId === roundId && p.userId === userId);
    if (existing) return;
    const participant: InputRoundParticipant = {
      id: generateUUID(),
      roundId,
      userId,
      userName,
      invitedAt: new Date(),
      submittedAt: null,
    };
    const result = await insertRecord<InputRoundParticipant>(PARTICIPANTS_TABLE, participant);
    if (result.error) throw new Error(result.error);
    set((s) => ({ participants: [...s.participants, participant] }));
  },

  removeParticipant: async (participantId) => {
    if (!isSupabaseAvailable()) throw new Error('Database not available');
    const { getSafeSupabaseClient } = await import('../../lib/supabaseSync');
    const supabase = getSafeSupabaseClient();
    if (!supabase) throw new Error('Database client unavailable');
    const { error } = await supabase.from(PARTICIPANTS_TABLE).delete().eq('id', participantId);
    if (error) throw new Error(error.message);
    set((s) => ({ participants: s.participants.filter((p) => p.id !== participantId) }));
  },

  markParticipantSubmitted: async (roundId, userId) => {
    if (!isSupabaseAvailable()) return;
    const participant = get().participants.find((p) => p.roundId === roundId && p.userId === userId);
    if (!participant || participant.submittedAt) return;
    const submittedAt = new Date();
    const result = await updateRecord<InputRoundParticipant>(PARTICIPANTS_TABLE, participant.id, {
      submittedAt,
    } as Partial<InputRoundParticipant>);
    if (result.error) return;
    set((s) => ({
      participants: s.participants.map((p) =>
        p.id === participant.id ? { ...p, submittedAt } : p
      ),
    }));
  },

  // ===========================
  // Queries
  // ===========================
  getRoundById: (id) => get().rounds.find((r) => r.id === id),

  getItemsForRound: (roundId) => get().roundItems.filter((i) => i.roundId === roundId),

  getParticipantsForRound: (roundId) => get().participants.filter((p) => p.roundId === roundId),

  getResponseRate: (roundId) => {
    const all = get().participants.filter((p) => p.roundId === roundId);
    const responded = all.filter((p) => p.submittedAt != null).length;
    const total = all.length;
    return { responded, total, percent: total > 0 ? Math.round((responded / total) * 100) : 0 };
  },
}));

export default useInputRoundsStore;
